/**
 * Sprint Redesign v1 · Runde 10 (Fr 04.09.2026, 05:00).
 *
 * Andreas' Flexpfad-Dashboard-Dynamik — 6 Bereiche in einer Sektion:
 *   1. Dynamik   (Wochen-Counter + Total)
 *   2. Muster    (Fokustyp-Verteilung letzter 30 Tage)
 *   3. Empfehlung (regelbasiert vom Backend)
 *   4. Heatmap   (28 Tage horizontales Grid)
 *   5. Letzte Fahrten (letzte 5 Einträge, klickbar)
 *   6. Streak    (Tage-in-Folge + nächstes Milestone)
 *
 * Backend: GET /api/dashboard/flexpfad-summary
 */
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Activity, Flame, TrendingUp, Compass, Sparkles, ArrowRight, Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import { FokustypIcon } from "@/components/redesign/FokustypIcon";

function KachelHeader({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[var(--fts-primary-bright)] mb-2">
      <Icon className="w-3.5 h-3.5" aria-hidden="true" />
      {label}
    </div>
  );
}

function DynamikKachel({ total, weekCount }) {
  return (
    <div
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5"
      data-testid="flexpfad-kachel-dynamik"
    >
      <KachelHeader icon={Activity} label="Aktivitäten" />
      <p className="text-3xl font-extrabold text-slate-900 dark:text-white">
        {weekCount}
      </p>
      <p className="text-sm text-slate-600 dark:text-slate-300">
        {weekCount === 1 ? "Flexpfad" : "Flexpfade"} diese Woche
      </p>
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
        Gesamt bisher: <b className="text-slate-700 dark:text-slate-200">{total}</b>
      </p>
    </div>
  );
}

function MusterKachel({ fokustypCounts, topFokustyp }) {
  const navigate = useNavigate();
  const total = Object.values(fokustypCounts).reduce((s, n) => s + n, 0);
  const NAMES = { hund: "Hund", eule: "Eule", katze: "Katze", biene: "Biene" };
  return (
    <div
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5"
      data-testid="flexpfad-kachel-muster"
    >
      <KachelHeader icon={TrendingUp} label="Muster · letzte Woche" />
      {total === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">Noch keine Muster erkennbar.</p>
      ) : (
        <>
          <p className="text-sm text-slate-700 dark:text-slate-200">
            Womit du dich diese Woche meist beschäftigt hast:
          </p>
          <div className="mt-3 space-y-1.5">
            {["hund", "eule", "katze", "biene"].map((k) => {
              const pct = total > 0 ? Math.round((fokustypCounts[k] / total) * 100) : 0;
              return (
                <div key={k} className="flex items-center gap-2 text-xs" data-testid={`flexpfad-muster-${k}`}>
                  <div className="w-14 flex items-center gap-1">
                    <FokustypIcon slug={k} size={14} />
                    <span className="font-semibold text-slate-700 dark:text-slate-200">{NAMES[k]}</span>
                  </div>
                  <div className="flex-1 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[var(--fts-primary-bright)]"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="w-8 text-right font-mono text-slate-500 dark:text-slate-400">{pct}%</span>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-[11px] text-slate-500 dark:text-slate-400 italic leading-snug">
            Das ist eine Beobachtung, keine Empfehlung — es zeigt nur womit du dich beschäftigt hast.
          </p>
          {topFokustyp && (
            <button
              type="button"
              onClick={() => navigate(`/dashboard/fokustyp/${topFokustyp}`)}
              data-testid="flexpfad-muster-cta"
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-[var(--fts-primary-bright)] hover:underline"
            >
              Steckbrief {NAMES[topFokustyp]} öffnen <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </>
      )}
    </div>
  );
}

function EmpfehlungKachel({ recommendation }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(recommendation.cta_to)}
      className="text-left rounded-2xl bg-[var(--fts-primary-bright)]/10 border border-[var(--fts-primary-bright)]/30 p-5 hover:border-[var(--fts-primary-bright)] hover:shadow-md transition-all w-full"
      data-testid="flexpfad-kachel-empfehlung"
    >
      <KachelHeader icon={Sparkles} label="Nächste Schritte für dich" />
      <p className="text-base font-extrabold text-slate-900 dark:text-white leading-snug" data-testid="flexpfad-empfehlung-title">
        {recommendation.title}
      </p>
      <p className="mt-1 text-sm text-slate-700 dark:text-slate-200 leading-snug" data-testid="flexpfad-empfehlung-body">
        {recommendation.body}
      </p>
      <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-[var(--fts-primary-bright)]">
        {recommendation.cta_label} <ArrowRight className="w-3.5 h-3.5" />
      </span>
    </button>
  );
}

function HeatmapKachel({ situationMatrix }) {
  const navigate = useNavigate();
  const SITUATIONS = ["privat", "beruflich", "freunde", "alltag"];
  const FOKUSTYPEN = ["hund", "eule", "katze", "biene"];
  const SIT_LABELS = { privat: "Familie / Privat", beruflich: "Beruflich", freunde: "Freunde", alltag: "Alltag" };
  const FT_LABELS = { hund: "Hund", eule: "Eule", katze: "Katze", biene: "Biene" };
  const all = FOKUSTYPEN.flatMap((ft) => SITUATIONS.map((s) => (situationMatrix?.[s]?.[ft] || 0)));
  const max = Math.max(1, ...all);
  const total = all.reduce((s, n) => s + n, 0);
  const level = (v) => {
    if (v === 0) return "bg-slate-100 dark:bg-slate-800";
    const ratio = v / max;
    if (ratio > 0.66) return "bg-[var(--fts-primary-bright)]";
    if (ratio > 0.33) return "bg-[var(--fts-primary-bright)]/70";
    return "bg-[var(--fts-primary-bright)]/40";
  };
  return (
    <div
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 lg:col-span-2"
      data-testid="flexpfad-kachel-heatmap"
    >
      <KachelHeader icon={Clock} label="Heatmap · Situation × Fokustyp" />
      {total === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="flexpfad-heatmap-empty">
          Sobald du deinen ersten Flexpfad baust, erscheint hier deine Verteilung.
        </p>
      ) : (
        <div>
          <div className="grid gap-1" style={{ gridTemplateColumns: "auto repeat(4, minmax(0, 1fr))" }}>
            <div />
            {SITUATIONS.map((s) => (
              <div key={s} className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 text-center px-1">
                {SIT_LABELS[s]}
              </div>
            ))}
            {FOKUSTYPEN.map((ft) => (
              <React.Fragment key={ft}>
                <div className="flex items-center gap-1 pr-2">
                  <FokustypIcon slug={ft} size={14} />
                  <span className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">{FT_LABELS[ft]}</span>
                </div>
                {SITUATIONS.map((s) => {
                  const v = situationMatrix?.[s]?.[ft] || 0;
                  return (
                    <button
                      key={`${ft}-${s}`}
                      type="button"
                      onClick={() => navigate(`/anliegen?modus=${s}&fokustyp=${ft}`)}
                      data-testid={`flexpfad-heatmap-cell-${ft}-${s}`}
                      title={`${v} Flexpfad${v === 1 ? "" : "e"} · ${FT_LABELS[ft]} in ${SIT_LABELS[s]}`}
                      className={["h-8 rounded flex items-center justify-center text-[10px] font-bold transition-transform hover:scale-105", level(v), v > 0 ? "text-white" : "text-slate-400 dark:text-slate-500"].join(" ")}
                    >
                      {v > 0 ? v : ""}
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-slate-500 dark:text-slate-400">
            Zellen sind klickbar — öffnen ein neues Anliegen mit vorausgewählter Situation.
          </p>
        </div>
      )}
    </div>
  );
}

function LetzteFahrtenKachel({ recent }) {
  const NAMES = { hund: "Hund", eule: "Eule", katze: "Katze", biene: "Biene" };
  return (
    <div
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 lg:col-span-2"
      data-testid="flexpfad-kachel-letzte-fahrten"
    >
      <KachelHeader icon={Compass} label="Letzte Flexpfade" />
      {recent.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Noch keine Flexpfade angelegt. <Link to="/anliegen" className="text-[var(--fts-primary-bright)] font-bold hover:underline">Erstes Anliegen starten →</Link>
        </p>
      ) : (
        <ul className="space-y-2.5">
          {recent.map((r) => {
            const d = new Date(r.created_at);
            const dateStr = d.toLocaleDateString("de-DE", { day: "2-digit", month: "short" });
            return (
              <li key={r.id} className="flex items-start gap-3" data-testid={`flexpfad-recent-${r.id}`}>
                <div className="flex-shrink-0 mt-0.5">
                  {r.fokustyp ? <FokustypIcon slug={r.fokustyp} size={18} /> : <Compass className="w-4 h-4 text-slate-400" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-800 dark:text-slate-200 leading-snug truncate">
                    {r.situation || "(keine Situation gespeichert)"}
                  </p>
                  <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                    {dateStr} {r.fokustyp && ` · ${NAMES[r.fokustyp] || r.fokustyp}`}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function StreakKachel({ streakDays, milestoneIn }) {
  return (
    <div
      className={[
        "rounded-2xl border p-5",
        streakDays > 0
          ? "bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-800"
          : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800",
      ].join(" ")}
      data-testid="flexpfad-kachel-streak"
    >
      <KachelHeader icon={Flame} label="Serie" />
      <p className="text-3xl font-extrabold text-slate-900 dark:text-white" data-testid="flexpfad-streak-days">
        {streakDays}
      </p>
      <p className="text-sm text-slate-600 dark:text-slate-300">
        {streakDays === 1 ? "Tag" : "Tage"} in Folge aktiv
      </p>
      {streakDays > 0 && milestoneIn > 0 && (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400" data-testid="flexpfad-streak-milestone">
          Noch <b className="text-orange-600 dark:text-orange-400">{milestoneIn}</b> {milestoneIn === 1 ? "Tag" : "Tage"} bis zum nächsten Meilenstein.
        </p>
      )}
      {streakDays === 0 && (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          Starte heute — auch 1 Flexpfad zählt.
        </p>
      )}
    </div>
  );
}

export default function FlexpfadVerdichtung() {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/dashboard/flexpfad-summary")
      .then((res) => { if (alive) setSummary(res.data); })
      .catch((e) => { if (alive) setError(e?.response?.status || "err"); });
    return () => { alive = false; };
  }, []);

  if (error || !summary) {
    return (
      <section className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5" data-testid="flexpfad-verdichtung-loading">
        <KachelHeader icon={Compass} label="Flexpfad-Dynamik" />
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {error ? "Konnte nicht geladen werden." : "Lade …"}
        </p>
      </section>
    );
  }

  return (
    <section
      className="space-y-3"
      data-testid="flexpfad-verdichtung"
      data-total={summary.total}
      data-week={summary.week_count}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
            Deine Flexpfad-Dynamik
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Was ist entstanden, welche Muster zeigen sich, was steht als nächstes an.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <DynamikKachel total={summary.total} weekCount={summary.week_count} />
        <StreakKachel streakDays={summary.streak_days} milestoneIn={summary.streak_milestone_in} />
        <MusterKachel fokustypCounts={summary.fokustyp_counts} topFokustyp={summary.top_fokustyp} />
        <EmpfehlungKachel recommendation={summary.recommendation} />
        <HeatmapKachel situationMatrix={summary.situation_matrix} />
        <LetzteFahrtenKachel recent={summary.recent} />
      </div>
    </section>
  );
}
